import { wallTimeToUtcMs } from '@kiips/ui';

// Re-export the promoted helper so existing call sites under
// apps/mobile/src/lib keep working. Source of truth lives in
// libs/ui/src/utils/timezone.ts per ADR-0076.
export { wallTimeToUtcMs };

/**
 * Resolve the IANA timezone for a coordinate pair. Returns `undefined`
 * when coordinates are missing or out-of-range — the caller falls back
 * to no-timezone (legacy floating-time) rendering rather than guessing.
 *
 * tz-lookup (~18 KB) is loaded lazily via dynamic import so it stays out
 * of the shared web bundle — it's only needed here, when picking a
 * journey anchor's location, never on first paint. Invalid coords return
 * synchronously without touching the chunk. tz-lookup throws on invalid
 * input; we catch so a malformed Google Places result can't crash the
 * journey edit flow.
 */
export async function tzFromCoords(
  lat: number | undefined,
  lng: number | undefined,
): Promise<string | undefined> {
  if (typeof lat !== 'number' || typeof lng !== 'number') return undefined;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return undefined;
  try {
    const { default: tzlookup } = await import('tz-lookup');
    return tzlookup(lat, lng);
  } catch {
    return undefined;
  }
}

/**
 * Signed minutes the destination timezone is ahead of (+) or behind (−)
 * the origin timezone at `atIso`. Positive means "set your watch
 * forward going from origin to destination."
 *
 * Computed by converting the same wall time in both zones to UTC and
 * diffing — DST-aware via `wallTimeToUtcMs`. Returns null when either
 * tz is missing/invalid or when the zones resolve to the same offset
 * at the chosen instant (e.g. both UTC+0 in winter).
 */
export function tzOffsetDeltaMinutes(
  originTz: string | undefined,
  destTz: string | undefined,
  atIso: string,
): number | null {
  if (!originTz || !destTz || originTz === destTz) return null;
  const oUtc = wallTimeToUtcMs(atIso, originTz);
  const dUtc = wallTimeToUtcMs(atIso, destTz);
  if (oUtc == null || dUtc == null) return null;
  const minutes = Math.round((oUtc - dUtc) / 60_000);
  return minutes === 0 ? null : minutes;
}

/** Short signed delta string ("+9h", "−5h 30m", "+45m"). Empty for 0
 *  or NaN. Uses U+2212 minus for visual parity with the '+' glyph. */
export function formatTzOffsetDelta(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes === 0) return '';
  const sign = minutes > 0 ? '+' : '−';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${sign}${m}m`;
  if (m === 0) return `${sign}${h}h`;
  return `${sign}${h}h ${m}m`;
}

/**
 * Short name for a timezone at a given instant ("EST", "EDT", "CET",
 * "GMT+5", etc). Falls back to the IANA name on platforms / locales
 * that don't carry abbreviations (rare, but Intl is permitted to).
 */
export function tzShortName(timezone: string, atIso?: string | undefined): string {
  const date = atIso ? new Date(atIso) : new Date();
  if (Number.isNaN(date.getTime())) return timezone;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value;
    return tzPart || timezone;
  } catch {
    return timezone;
  }
}
